import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '../services/authService';
import toast from 'react-hot-toast';

// Initial state
const initialState = {
  user: null,
  loading: true,
  error: null,
};

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  UPDATE_USER: 'UPDATE_USER',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        loading: true,
        error: null,
      };
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload,
        loading: false,
        error: null,
      };
    
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        loading: false,
        error: action.payload,
      };
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        loading: false,
        error: null,
      };
    
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    
    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: action.payload,
      };
    
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
    
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext(null);

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Session timeout configuration (30 minutes in milliseconds)
  const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  const CHECK_INTERVAL = 60 * 1000; // Check every 1 minute

  // Track last activity time
  useEffect(() => {
    const updateLastActivity = () => {
      localStorage.setItem('lastActivity', Date.now().toString());
    };

    // Update on mount
    updateLastActivity();

    // Track user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, updateLastActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateLastActivity);
      });
    };
  }, []);

  // Check for session timeout
  useEffect(() => {
    if (!state.user) return;

    const checkSessionTimeout = () => {
      const lastActivity = localStorage.getItem('lastActivity');
      if (!lastActivity) {
        localStorage.setItem('lastActivity', Date.now().toString());
        return;
      }

      const timeSinceActivity = Date.now() - parseInt(lastActivity, 10);
      
      if (timeSinceActivity > SESSION_TIMEOUT) {
        console.warn('[AuthContext] Session timed out due to inactivity');
        toast.error('Your session has expired due to inactivity. Please log in again.');
        logout();
      }
    };

    // Check immediately
    checkSessionTimeout();

    // Check periodically
    const interval = setInterval(checkSessionTimeout, CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [state.user]);

  // Check for existing session on app start
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (token) {
        try {
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
          const user = await authService.getProfile();
          dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: user });
        } catch (error) {
          // Only clear token if it's actually expired/invalid, not for network errors
          const is401 = error.response?.status === 401;
          const isExpired = error.response?.data?.error?.includes('expired') || 
                           error.response?.data?.message?.includes('expired');
          
          if (is401 || isExpired) {
            // Token is invalid, remove it
            localStorage.removeItem('token');
            dispatch({ type: AUTH_ACTIONS.LOGOUT });
          } else {
            // Network error or other issue - keep token and retry later
            console.warn('Failed to fetch user profile, but keeping session:', error);
            dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
          }
        }
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    initializeAuth();
  }, []);

  // Periodically verify session is still valid (every 5 minutes)
  useEffect(() => {
    if (!state.user) return;

    const verifySession = async () => {
      try {
        const user = await authService.getProfile();
        // Silently update user data if it changed
        if (JSON.stringify(user) !== JSON.stringify(state.user)) {
          dispatch({ type: AUTH_ACTIONS.UPDATE_USER, payload: user });
        }
      } catch (error) {
        // Only logout on auth errors, not network errors
        if (error.response?.status === 401) {
          console.warn('Session expired, logging out');
          logout();
        }
      }
    };

    // Check session every 5 minutes
    const interval = setInterval(verifySession, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [state.user]);

  // Login function
  const login = async (email, password) => {
    try {
      dispatch({ type: AUTH_ACTIONS.LOGIN_START });
      
      const response = await authService.login(email, password);
      
      // Check if 2FA is required
      if (response.requiresTwoFactor) {
        dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: '2FA verification required' });
        return { 
          success: false, 
          requiresTwoFactor: true,
          tempToken: response.tempToken,
          message: response.message
        };
      }
      
      // Store token in localStorage
      if (response.token) {
        localStorage.setItem('token', response.token);
      }
      
      // Update state with user data
      dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: response.user });
      
      toast.success(`Welcome back, ${response.user.firstName || response.user.email}!`);
      
      return { success: true, user: response.user };
    } catch (error) {
      console.error('[AuthContext] Login error:', error);
      
      // Extract detailed error information
      const errorData = error.response?.data;
      let errorMessage = 'Login failed. Please try again.';
      let errorCode = null;
      let userEmail = email; // Store email for redirect
      
      if (errorData) {
        // Check for specific error codes
        if (errorData.error?.code === 'USER_NOT_FOUND') {
          errorMessage = 'No account found with this email address. Please check your email or register for a new account.';
          errorCode = 'USER_NOT_FOUND';
        } else if (errorData.error?.code === 'INVALID_PASSWORD') {
          errorMessage = 'The password you entered is incorrect. Please try again or reset your password.';
          errorCode = 'INVALID_PASSWORD';
        } else if (errorData.error?.code === 'EMAIL_NOT_VERIFIED') {
          errorMessage = errorData.error?.message || 'Please verify your email to continue. A verification code has been sent to your email.';
          errorCode = 'EMAIL_NOT_VERIFIED';
          // Return special flag for redirect - show one toast then redirect
          dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: errorMessage });
          toast.success('Verification code sent! Check your email.', { duration: 4000, icon: '📧' });
          return { 
            success: false, 
            error: errorMessage, 
            errorCode: 'EMAIL_NOT_VERIFIED',
            email: userEmail,
            requiresVerification: true 
          };
        } else {
          errorMessage = errorData.error?.message || errorData.message || errorMessage;
        }
      }
      
      dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: errorMessage });
      toast.error(errorMessage);
      return { success: false, error: errorMessage, errorCode, email: userEmail };
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.LOGIN_START });
      
      const response = await authService.register(userData);
      
      // DO NOT store token or log user in - they need to verify email first
      // User will get token after email verification
      
      dispatch({ type: AUTH_ACTIONS.LOGOUT }); // Ensure user is not logged in
      
      return { success: true, user: response.user };
    } catch (error) {
      // Extract specific error message from backend
      let errorMessage = 'Registration failed. Please try again.';
      
      if (error.response?.data) {
        if (error.response.data.error?.message) {
          errorMessage = error.response.data.error.message;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      }
      
      dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: errorMessage });
      // Don't show toast here - let the page component handle UI feedback
      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('lastActivity');
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
    toast.success('Logged out successfully');
  };

  // Update user profile
  const updateUser = async (userData) => {
    try {
      const updatedUser = await authService.updateProfile(userData);
      dispatch({ type: AUTH_ACTIONS.UPDATE_USER, payload: updatedUser });
      return { success: true, user: updatedUser };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update profile';
      return { success: false, error: errorMessage };
    }
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Context value
  const value = {
    user: state.user,
    loading: state.loading,
    error: state.error,
    login,
    register,
    logout,
    updateUser,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};